import type { FC } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';

import type { RevenueEntry } from '../types';
import KPIMetricCard from './KPIMetricCard';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  paper: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
    },
  },
  title: {
    fontWeight: 500,
    color: '#000000',
  },
  subtitle: {
    color: '#000000',
    marginTop: theme.spacing(0.5),
  },
  cardGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    },
  },
}));

type FootnoteConfig = {
  readonly text: string;
  readonly color: 'error' | 'textSecondary';
};

type MetricGroupProps = {
  readonly sectionId: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly entries: readonly RevenueEntry[];
  readonly labelOverrides?: Partial<Record<string, string>>;
  readonly footnoteMap?: Partial<Record<string, FootnoteConfig>>;
};

const MetricGroup: FC<MetricGroupProps> = ({
  title,
  subtitle,
  entries,
  labelOverrides,
  footnoteMap,
  sectionId,
}) => {
  const classes = useStyles();

  const sortedEntries = [...entries].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );

  return (
    <Box
      component="section"
      className={classes.container}
      aria-labelledby={sectionId}
    >
      <Paper elevation={1} className={classes.paper}>
        <Box component="header" className={classes.header}>
          <Typography variant="h6" component="h2" id={sectionId} className={classes.title}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box className={classes.cardGrid}>
          {sortedEntries.map((entry) => {
            const footnoteConfig = footnoteMap ? footnoteMap[entry.code] : undefined;
            return (
              <div key={entry.code}>
                <KPIMetricCard
                  entry={entry}
                  labelOverride={labelOverrides ? labelOverrides[entry.code] : undefined}
                  footnote={footnoteConfig ? footnoteConfig.text : undefined}
                  footnoteColor={footnoteConfig ? footnoteConfig.color : undefined}
                />
              </div>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export { MetricGroup };
export default MetricGroup;
